import React, { useState, useEffect, useMemo } from 'react';
import { Box, Flex, Text, Icon, Spinner } from '@chakra-ui/react';
import {
  FiArrowUpRight, FiArrowDownRight, FiChevronRight, FiTrendingUp,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatFigure } from '../../utils/formatters';
import { gapToPrevious } from '../../utils/spendingGap';
import { fetchSpendingTrend } from '../../api/endpointsBackend';
import { cardStyle, cardTitle, viewAllStyle } from './dashboardStyles';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import Figure from '../shared/Figure';

import TimeSeriesChart, { chartColor } from '../shared/TimeSeriesChart';

// Spending more than last period is the bad direction.
const TONE = {
  good: { color: 'redesign.amount.income', bg: 'rgba(21, 128, 61, 0.10)', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  bad: { color: 'redesign.accent.red', bg: 'rgba(185, 28, 28, 0.08)', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
};

/**
 * Spending Trend card — cumulative expenses over the selected period with
 * previous-period comparison. Needs a single series, so it hides for "all"
 * currencies — unless a display currency is set, which converts every currency
 * into one and makes "all" plottable again.
 */
export default function DashboardSpendingTrend({
  selectedCurrency, displayCurrency, dateRange, apiFilters, apiFilterGroups, groupsOperator, onNavigate,
}) {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const converting = Boolean(displayCurrency);
  // "All" only blocks the chart when we're NOT converting — conversion collapses
  // the currency axis, so there's a single series to draw.
  const showEmpty = selectedCurrency === 'all' && !converting;
  const filtersKey = JSON.stringify({ apiFilters, apiFilterGroups, groupsOperator });

  // Spending trend is expenses-only — hide if type filter explicitly excludes Expenses
  const expensesFiltered = useMemo(() => {
    const typeFilter = (apiFilters || []).find(f => f.column === 'type');
    if (!typeFilter) return false;
    return !typeFilter.values?.includes('Expenses');
  }, [apiFilters]);

  useEffect(() => {
    if (showEmpty || expensesFiltered) { setData(null); setLoading(false); return; }
    setLoading(true);
    fetchSpendingTrend(dateRange?.start_date || null, dateRange?.end_date || null, selectedCurrency, apiFilters, apiFilterGroups, groupsOperator, displayCurrency)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmpty, expensesFiltered, selectedCurrency, displayCurrency, dateRange?.start_date, dateRange?.end_date, filtersKey]);

  const deltaUp = data?.delta > 0;
  const currentPoints = data?.current?.points || [];
  const previousPoints = data?.previous?.points || [];
  const gap = gapToPrevious(currentPoints, previousPoints);

  // The tag on the line's last point: green when spending is under the
  // previous period at the same day, red when it is over.
  const endTag = gap === null || Math.round(gap) === 0 ? undefined : (
    <Text
      as="span"
      display="inline-block"
      px="7px"
      lineHeight="20px"
      borderRadius="6px"
      fontSize="xs"
      fontWeight={600}
      textStyle="tabularNumbers"
      whiteSpace="nowrap"
      {...(gap > 0
        ? { bg: 'redesign.chip.positive.bg', color: 'redesign.chip.positive.fg' }
        : { bg: 'redesign.accent.red', color: 'white', _dark: { color: '#05080E' } })}
    >
      {t(gap > 0 ? 'spendingTrend.under' : 'spendingTrend.over', { amount: formatFigure(gap) })}
    </Text>
  );

  return (
    <Box {...cardStyle}>
      <Flex justify="space-between" align="center" mb={3} gap={2}>
        <Text {...cardTitle} minW={0}>{t('spendingTrend.title')}</Text>
        <Text {...viewAllStyle} onClick={() => onNavigate('movements')} flexShrink={0}>{t('spendingTrend.viewAll')} <Icon as={FiChevronRight} w="14px" h="14px" /></Text>
      </Flex>
      {showEmpty ? (
        <Flex direction="column" align="center" justify="center" h="180px" gap={2}>
          <Icon as={FiTrendingUp} w="32px" h="32px" color="redesign.text.faint" />
          <Text fontSize="md" color="redesign.text.muted">{t('spendingTrend.selectCurrency')}</Text>
        </Flex>
      ) : expensesFiltered ? (
        <Flex direction="column" align="center" justify="center" h="180px" gap={2}>
          <Icon as={FiTrendingUp} w="32px" h="32px" color="redesign.text.faint" />
          <Text fontSize="md" color="redesign.text.muted">{t('spendingTrend.expensesOnly')}</Text>
        </Flex>
      ) : loading || !data ? (
        <Flex direction="column" align="center" justify="center" h="180px" gap={2}>
          <Spinner size="sm" color="redesign.text.muted" />
        </Flex>
      ) : (
        <>
          <Flex align="center" gap={3} mb={1} wrap="wrap">
            <Figure code={data.currency} amount={formatMoney(data.current.total, data.currency, true)} />
            {data.delta_pct !== null && data.delta_pct !== undefined && (
              <Flex align="center" gap={1} px={1.5} h="22px" borderRadius="6px" {...TONE[deltaUp ? 'bad' : 'good']}>
                <Icon as={deltaUp ? FiArrowUpRight : FiArrowDownRight} w="12px" h="12px" />
                <Text fontSize="2xs" fontWeight={600} color="inherit" textStyle="tabularNumbers">
                  {deltaUp ? '+' : ''}{data.delta_pct}%
                </Text>
              </Flex>
            )}
          </Flex>
          {data.previous?.total > 0 && (
            <Text fontSize="sm" color="redesign.text.muted" mb={2} noOfLines={1} title={t('spendingTrend.tooltip', { amount: formatMoney(data.previous.total, data.currency), label: data.previous.label })}>
              {t('spendingTrend.previousLabel', { amount: formatMoney(data.previous.total, data.currency), label: data.previous.label })}
            </Text>
          )}
          <TimeSeriesChart
            height={110}
            my={0}
            periods={currentPoints.map((pt) => pt.date)}
            granularity="day"
            // A period still under way stops part-way across the previous one.
            slots={previousPoints.length ? Math.max(currentPoints.length, previousPoints.length) : undefined}
            series={[
              // the previous period stays dashed: it is a comparison, not this period's line
              ...(previousPoints.length
                ? [{ key: 'previous', values: previousPoints.map((pt) => pt.cumulative),
                     color: chartColor('redesign.chart.previous'), strokeWidth: 1.5, dashed: true, endDot: false }]
                : []),
              { key: 'current', values: currentPoints.map((pt) => pt.cumulative),
                color: chartColor('redesign.accent.expense'), endGlow: true, endTag },
            ]}
          />
          <Flex align="center" gap={4} mt={1}>
            <Flex align="center" gap={1.5}>
              <Box w="16px" h="2px" borderRadius="sm" bg="redesign.accent.expense" />
              <Text fontSize="2xs" color="redesign.text.muted">{data.current.label}</Text>
            </Flex>
            {previousPoints.length > 0 && (
              <Flex align="center" gap={1.5}>
                <Box w="16px" h="2px" borderRadius="sm"
                  bgImage={`repeating-linear-gradient(90deg,${chartColor('redesign.chart.previous')} 0 3px,transparent 3px 6px)`} />
                <Text fontSize="2xs" color="redesign.text.muted">{data.previous.label}</Text>
              </Flex>
            )}
          </Flex>
          {data.num_unconvertible > 0 && (
            <Box mt={2}>
              <UnconvertibleNote buckets={data.unconvertible} count={data.num_unconvertible} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
